import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { connectToSalesforce } from '../services/salesforceService'

export default function ConnectSalesforceEnhanced() {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [environment, setEnvironment] = useState('production')
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)
  const navigate = useNavigate()
  
  // Initialize environment from localStorage if available
  useEffect(() => {
    const savedEnv = localStorage.getItem('sf_env');
    if (savedEnv === 'sandbox' || savedEnv === 'production') {
      setEnvironment(savedEnv);
    }
  }, [])
  
  const handleConnectSalesforce = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      if (!user) {
        setError('You must be logged in to connect a Salesforce org')
        setIsLoading(false)
        return
      }
      
      // Ensure we have our consumer key
      const consumerKey = import.meta.env.VITE_SALESFORCE_CONSUMER_KEY;
      if (!consumerKey) {
        setError('Salesforce Consumer Key is not configured. Check your .env file.');
        setIsLoading(false);
        return;
      }
      
      // Store user authentication state and environment in localStorage before redirect
      localStorage.setItem('sf_auth_state', 'connecting');
      localStorage.setItem('sf_auth_user', user.id);
      localStorage.setItem('sf_env', environment);
      
      console.log(`Connecting to Salesforce ${environment} environment`);
      
      // Initiate Salesforce OAuth flow
      try {
        connectToSalesforce(environment)
        
        // If we're still here after 2 seconds, the redirect probably failed
        setTimeout(() => {
          if (document.hasFocus()) {
            setError('Redirect to Salesforce failed. Try the alternative direct link below.');
            setIsLoading(false);
          }
        }, 2000);
      } catch (redirectErr) {
        console.error('Error during redirect:', redirectErr);
        setError('Failed to redirect to Salesforce. Try the alternative link.');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Error initiating Salesforce connection:', err)
      setError('Failed to connect to Salesforce. Please try again.')
      setIsLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Connect to Salesforce
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Connect your Salesforce organization to document your flows
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {/* Salesforce Enhanced Security Policy Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <div>
                  <p className="text-sm text-blue-800">
                    <strong>Salesforce Enhanced Security Update:</strong>
                    <br />
                    FlowDocs strongly supports Salesforce's new security policies requiring admin approval for Connected Apps. If you are unable to connect, contact your Salesforce admin for assistance.
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLearnMoreModal(true);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 underline mt-2 font-medium"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-md mb-6">
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-2">When you connect your Salesforce org:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>We'll only access your Flow metadata - not your actual data</li>
                <li>We'll sync your Flow definitions to create documentation</li>
                <li>You can disconnect at any time</li>
                <li>Your credentials are securely stored and encrypted</li>
              </ul>
            </div>
          </div>
          
          {/* Environment selection */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Salesforce Environment
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-blue-600"
                  name="environment"
                  value="production"
                  checked={environment === 'production'}
                  onChange={() => setEnvironment('production')}
                />
                <span className="ml-2 text-sm text-gray-700">Production</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio h-4 w-4 text-blue-600"
                  name="environment"
                  value="sandbox"
                  checked={environment === 'sandbox'}
                  onChange={() => setEnvironment('sandbox')}
                />
                <span className="ml-2 text-sm text-gray-700">Sandbox</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {environment === 'production' 
                ? 'Connect to your production Salesforce organization (login.salesforce.com)'
                : 'Connect to your Salesforce sandbox environment (test.salesforce.com)'}
            </p>
          </div>
          
          <div>
            <button
              onClick={handleConnectSalesforce}
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {isLoading ? 'Connecting...' : `Connect to Salesforce ${environment === 'sandbox' ? 'Sandbox' : 'Production'}`}
            </button>
          </div>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Learn More Modal */}
      {showLearnMoreModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Connecting to Salesforce: Admin Requirements
                </h2>
                <button
                  onClick={() => setShowLearnMoreModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Why This Is Required</h3>
                <p className="text-sm text-gray-700">
                  Salesforce's September 2025 security updates now require administrative approval for third-party apps like FlowDocs.
                  This protects your organization from unauthorized integrations.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Who Can Connect</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>
                    <span className="font-medium">• Salesforce Administrators - </span>
                    Can connect immediately (you have required permissions by default)
                  </li>
                  <li>
                    <span className="font-medium">• Other Users - </span>
                    Need the "Approve Uninstalled Connected Apps" permission from your admin
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">If You're Not an Admin</h3>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Option 1: Request Permission</h4>
                    <p className="text-sm text-gray-700">
                      Ask your Salesforce admin to grant you the "Approve Uninstalled Connected Apps" permission through a Permission Set.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Option 2: Ask Admin to Connect</h4>
                    <p className="text-sm text-gray-700">
                      Your admin can connect FlowDocs themselves, which enables access for your entire organization.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Note:</strong> You'll need to{' '}
                      <button
                        onClick={() => {
                          setShowLearnMoreModal(false);
                          navigate('/settings?tab=team');
                        }}
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        invite your Salesforce admin
                      </button>{' '}
                      to FlowDocs first. Be sure to set their role to Admin
                      so they have the necessary permissions to make the connection.
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mt-3">
                  Once an Org is connected, it will be available to any FlowDocs users in your account.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowLearnMoreModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}